/**
 * The report-level date range.
 *
 * A thin binding over the kit's `DateRangePicker`: the kit owns the popover,
 * the Monday-first preset maths and the custom-range validation; this file owns
 * only which presets a REPORT offers and how they are captioned.
 *
 * Seven presets, not the kit's ten. `last-90` and `last-7` are dropped because
 * the settling rhythm of this business is daily → weekly → monthly and every
 * extra row in a list of dates is a row to read past; `last-month` is dropped
 * because `this-month`/`custom` already reach it and the operator asking "what
 * did June do" is doing a one-off, not a routine. `last-week` earns the top of
 * that list: it is the week being settled on a Monday, which is the single most
 * frequently answered question on these five screens.
 */

import {
  DateRangePicker,
  resolveRangePreset,
  type DateRangePresetId,
  type DateRangeValue,
} from '@w7/ui'
import { ll } from '@/lib/format'

/** The presets a report offers, in the order an operator reaches for them. */
export const REPORT_PRESETS: DateRangePresetId[] = [
  'today',
  'yesterday',
  'this-week',
  'last-week',
  'this-month',
  'last-30',
  'custom',
]

/**
 * `ll` on an ISO DAY, built from local parts.
 *
 * `new Date('2026-07-20')` is UTC midnight, which renders as the 19th for any
 * operator west of Greenwich — the label would disagree with the `from` param
 * sitting in the URL beside it. IST hides this entirely; a browser in Los
 * Angeles does not. Still formatted by `ll`, so there is one date format.
 */
function llDay(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  return y && m && d ? ll(new Date(y, m - 1, d)) : ll(iso)
}

/** "20 Jul 2026 – 26 Jul 2026", or one date when the range is a single day. */
export function rangeLabel(range: DateRangeValue): string {
  return range.from === range.to ? llDay(range.from) : `${llDay(range.from)} – ${llDay(range.to)}`
}

/**
 * Infer which preset produced a range.
 *
 * The URL carries `from`/`to` as well as `preset` so a shared link reproduces
 * the exact dates rather than re-resolving "last week" in the reader's week.
 * That leaves the reverse problem — a range arriving with no preset at all (the
 * shell's own range, on first load) — and matching it back gives the trigger a
 * name to show instead of the always-truthful-but-useless "Custom".
 */
export function presetOf(range: DateRangeValue, today?: Date): DateRangePresetId {
  for (const id of REPORT_PRESETS) {
    const resolved = resolveRangePreset(id, today)
    if (resolved && resolved.from === range.from && resolved.to === range.to) return id
  }
  return 'custom'
}

export interface RangePickerProps {
  preset: DateRangePresetId
  value: DateRangeValue
  onChange: (next: { preset: DateRangePresetId } & DateRangeValue) => void
  align?: 'start' | 'end'
  /** Injectable reference day, so a test does not have to fake the clock. */
  today?: Date
}

export function RangePicker({ preset, value, onChange, align = 'start', today }: RangePickerProps) {
  return (
    <DateRangePicker
      preset={preset}
      value={value}
      onChange={onChange}
      presets={REPORT_PRESETS}
      align={align}
      today={today}
      summary={rangeLabel(value)}
      label="Report date range"
      presetHint={(id, resolved) => {
        if (!resolved) return null
        const dates = rangeLabel(resolved)
        // Only one preset gets an explanation, and it is the one whose meaning
        // is a business rule rather than a date calculation.
        return id === 'last-week' ? (
          <span title={`${dates} — the week you settle on Monday.`}>{dates}</span>
        ) : (
          dates
        )
      }}
    />
  )
}
